import { useEffect, useState, useCallback } from "react";
import { Check, Lightning, Rocket, Buildings, Gift, PaypalLogo, CreditCard, SealCheck, XCircle, Receipt, DownloadSimple } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";

const ICONS = { trial: Gift, starter: Lightning, pro: Rocket, agency: Buildings };
const FEATURES = {
  trial: ["3 repurposes / month", "All platforms", "1 workspace", "Brand voice tuning"],
  starter: ["15 repurposes / month", "All platforms + clips", "1 workspace", "Scheduler & calendar"],
  pro: ["60 repurposes / month", "Everything in Starter", "3 workspaces", "Priority generation"],
  agency: ["200 repurposes / month", "Everything in Pro", "20 workspaces", "Multi-brand agency mode"],
};
const ORDER = ["trial", "starter", "pro", "agency"];

export default function Billing() {
  const { active, activeId, refresh } = useWorkspace();
  const [plans, setPlans] = useState({});
  const [prices, setPrices] = useState({});
  const [cycle, setCycle] = useState("monthly");
  const [loadingKey, setLoadingKey] = useState(null);
  const [paypalKey, setPaypalKey] = useState(null);
  const [paypalConfigured, setPaypalConfigured] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [credits, setCredits] = useState({});
  const [history, setHistory] = useState([]);

  const loadSub = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/subscription?workspace_id=${activeId}`); setSubscription(data); } catch (e) { console.error("Failed to load subscription", e); }
  }, [activeId]);

  const loadHistory = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/payments/history?workspace_id=${activeId}`); setHistory(data); } catch {}
  }, [activeId]);

  useEffect(() => {
    api.get("/billing/plans").then((r) => { setPlans(r.data.plans || {}); setPrices(r.data.prices || {}); }).catch(() => {});
    api.get("/paypal/config").then((r) => setPaypalConfigured(r.data.configured)).catch(() => {});
  }, []);
  useEffect(() => { loadSub(); loadHistory(); }, [loadSub, loadHistory]);

  // Proration credits when an active paid subscription exists
  useEffect(() => {
    if (!activeId || !subscription?.active || Object.keys(prices).length === 0) { setCredits({}); return; }
    const tiers = ["starter", "pro", "agency"];
    Promise.all(tiers.map((t) => {
      const lk = `${t}_${cycle === "yearly" ? "yearly" : "monthly"}`;
      return api.get(`/subscription/proration?workspace_id=${activeId}&lookup_key=${lk}`)
        .then((r) => [t, r.data.credit]).catch(() => [t, 0]);
    })).then((pairs) => setCredits(Object.fromEntries(pairs)));
  }, [activeId, subscription, cycle, prices]);

  const lookupFor = (tier) => `${tier}_${cycle === "yearly" ? "yearly" : "monthly"}`;

  const startCheckout = async (tier) => {
    const lookup = lookupFor(tier);
    if (!prices[lookup] || !activeId) return;
    setLoadingKey(tier);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin, workspace_id: activeId });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setLoadingKey(null); }
  };

  const startPaypal = async (tier) => {
    const lookup = lookupFor(tier);
    if (!prices[lookup] || !activeId) return;
    setPaypalKey(tier);
    try {
      const { data } = await api.post("/paypal/subscribe", { lookup_key: lookup, origin_url: window.location.origin, workspace_id: activeId });
      if (data.approval_url) { window.location.href = data.approval_url; return; }
      // revised & applied immediately (no re-approval needed)
      toast.success("Plan updated via PayPal");
      await refresh(); await loadSub(); await loadHistory();
      setPaypalKey(null);
    } catch (e) { toast.error(e.response?.data?.detail || "PayPal is not available yet"); setPaypalKey(null); }
  };

  const downloadInvoice = async (ref) => {
    try {
      const res = await api.get(`/payments/invoice?workspace_id=${activeId}&ref=${encodeURIComponent(ref)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `postpilot-invoice-${String(ref).slice(-6)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download invoice"); }
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/subscription/cancel?workspace_id=${activeId}`);
      await refresh(); await loadSub(); await loadHistory();
      toast.success("Subscription cancelled — you're back on the Free plan");
      setShowCancel(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setCancelling(false); }
  };

  const currentPlan = active?.plan || "trial";
  const isTrialWorkspace = currentPlan === "trial";

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Billing &amp; Plans</h1>
        <div className="text-muted-foreground text-sm mt-2 flex items-center gap-1.5 flex-wrap">
          Plan for <span className="font-medium text-foreground">{active?.name}</span>:{" "}
          <Badge variant="secondary" className="uppercase font-mono">{currentPlan}</Badge>
        </div>
      </div>

      {/* Manage subscription */}
      {subscription?.active && (
        <div className="mb-8 rounded-lg border border-border bg-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="manage-subscription-card">
          <div className="w-11 h-11 rounded-md bg-chart-2/15 flex items-center justify-center shrink-0">
            <SealCheck size={22} weight="fill" className="text-chart-2" />
          </div>
          <div className="flex-1">
            <div className="font-medium flex items-center gap-2">
              {subscription.plan_name} <Badge variant="outline" className="font-mono text-xs">{subscription.interval === "year" ? "Yearly" : "Monthly"}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
              {subscription.provider === "paypal"
                ? <><PaypalLogo size={13} weight="fill" style={{ color: "#0070BA" }} /> Billed via PayPal</>
                : <><CreditCard size={13} /> Billed via card (Stripe)</>}
              · active since {subscription.since ? new Date(subscription.since).toLocaleDateString() : "—"}
            </p>
          </div>
          <Button variant="outline" data-testid="cancel-subscription-btn" onClick={() => setShowCancel(true)}
            className="text-destructive hover:text-destructive">
            <XCircle size={16} className="mr-1.5" /> Cancel subscription
          </Button>
        </div>
      )}

      {/* Cycle toggle */}
      <div className="flex items-center justify-center mb-8">
        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-card">
          <button data-testid="billing-cycle-monthly" onClick={() => setCycle("monthly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            Monthly
          </button>
          <button data-testid="billing-cycle-yearly" onClick={() => setCycle("yearly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            Yearly <span className={`text-xs ${cycle === "yearly" ? "text-primary-foreground/90" : "text-chart-2"}`}>save ~17%</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {ORDER.map((tier) => {
          const meta = plans[tier];
          if (!meta) return null;
          const Icon = ICONS[tier];
          const curInterval = subscription?.interval === "year" ? "year" : "month";
          const cycleInterval = cycle === "yearly" ? "year" : "month";
          const isCurrent = tier === currentPlan && (isTrialWorkspace || curInterval === cycleInterval);
          const highlight = tier === "pro";
          const lookup = lookupFor(tier);
          const yearlyAmt = prices[`${tier}_yearly`]?.amount;
          const monthlyAmt = prices[`${tier}_monthly`]?.amount;
          const isTrial = tier === "trial";
          const perMonth = isTrial ? 0 : (cycle === "yearly" ? (yearlyAmt / 12) : monthlyAmt);
          const savings = (!isTrial && monthlyAmt && yearlyAmt) ? (monthlyAmt * 12 - yearlyAmt) : 0;

          return (
            <div key={tier} data-testid={`plan-${tier}`}
              className={`rounded-lg border bg-card p-6 flex flex-col relative ${highlight ? "border-primary shadow-sm" : "border-border"}`}>
              {highlight && <Badge className="absolute -top-2.5 left-6 font-mono text-xs">MOST POPULAR</Badge>}
              <Icon size={26} className="text-primary mb-3" weight="fill" />
              <h3 className="font-heading font-bold text-xl">{meta.name}</h3>
              <div className="mt-3 mb-1">
                <span className="font-heading font-black text-4xl">${isTrial ? 0 : Math.round(perMonth)}</span>
                <span className="text-muted-foreground text-sm">/mo</span>
              </div>
              <div className="mb-5 h-5">
                {!isTrial && cycle === "yearly" && (
                  <span className="text-xs text-muted-foreground">${yearlyAmt} billed yearly · <span className="text-chart-2 font-medium">save ${savings}</span></span>
                )}
                {!isTrial && cycle === "monthly" && <span className="text-xs text-muted-foreground">billed monthly</span>}
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {FEATURES[tier].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check size={16} className="text-primary mt-0.5 shrink-0" weight="bold" /> {f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <Button variant="outline" disabled className="w-full" data-testid={`current-${tier}`}>Current plan</Button>
              ) : isTrial ? (
                <Button variant="outline" disabled className="w-full">Free</Button>
              ) : (
                <div className="space-y-2">
                  {credits[tier] > 0 && (
                    <div data-testid={`credit-${tier}`} className="text-xs text-chart-2 font-medium flex items-center gap-1">
                      <Check size={13} weight="bold" /> ${credits[tier]} credit for unused time applied
                    </div>
                  )}
                  <Button className="w-full" variant={highlight ? "default" : "outline"} data-testid={`upgrade-${tier}`}
                    onClick={() => startCheckout(tier)} disabled={loadingKey === tier}>
                    <CreditCard size={16} className="mr-1.5" /> {loadingKey === tier ? "Redirecting…" : "Pay with card"}
                  </Button>
                  <Button className="w-full" variant="outline" data-testid={`paypal-${tier}`}
                    onClick={() => startPaypal(tier)} disabled={paypalKey === tier || !paypalConfigured}
                    title={paypalConfigured ? "" : "PayPal not configured yet"}>
                    <PaypalLogo size={16} weight="fill" className="mr-1.5" style={{ color: "#0070BA" }} />
                    {paypalKey === tier ? "Redirecting…" : paypalConfigured ? "PayPal" : "PayPal (soon)"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-6 font-mono">
        Card test mode · 4242 4242 4242 4242. PayPal sandbox · use a sandbox buyer account.
      </p>

      {/* Payment history */}
      <div className="mt-10" data-testid="payment-history">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-4"><Receipt size={20} /> Payment history</h2>
        {history.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No payments yet. Your invoices will appear here.
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs font-mono uppercase text-muted-foreground">
                  <th className="text-left font-normal px-4 py-3">Date</th>
                  <th className="text-left font-normal px-4 py-3">Plan</th>
                  <th className="text-left font-normal px-4 py-3">Method</th>
                  <th className="text-right font-normal px-4 py-3">Amount</th>
                  <th className="text-right font-normal px-4 py-3">Status</th>
                  <th className="text-right font-normal px-4 py-3">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} data-testid={`history-row-${i}`} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 text-muted-foreground">{h.date ? new Date(h.date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 capitalize">
                      {h.tier || "—"} <span className="text-xs text-muted-foreground">{h.interval === "year" ? "/yr" : "/mo"}</span>
                      {h.credit_applied > 0 && <span className="ml-1.5 text-xs text-chart-2">−${h.credit_applied} credit</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {h.provider === "paypal"
                          ? <><PaypalLogo size={14} weight="fill" style={{ color: "#0070BA" }} /> PayPal</>
                          : <><CreditCard size={14} /> Card</>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{h.amount != null ? `$${h.amount}` : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge variant={h.status === "paid" ? "secondary" : h.status === "cancelled" ? "outline" : "outline"}
                        className="capitalize font-mono text-xs">{h.status || "pending"}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {h.status === "paid" && h.ref ? (
                        <button data-testid={`invoice-${i}`} onClick={() => downloadInvoice(h.ref)}
                          className="inline-flex items-center gap-1 text-primary hover:underline text-xs">
                          <DownloadSimple size={14} /> PDF
                        </button>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel subscription?</DialogTitle>
            <DialogDescription>
              You'll be moved back to the Free plan (3 repurposes/month) right away. You can re-subscribe anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Keep plan</Button>
            <Button variant="destructive" data-testid="confirm-cancel-btn" onClick={doCancel} disabled={cancelling}>
              {cancelling ? "Cancelling…" : "Yes, cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
